import { useState } from 'react'
import { Clock, Sparkles, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react'
import { useSpecificationVersions, useCreateSpecificationVersion } from '../../api/leads'
import { cn, formatDate } from '../../lib/utils'

interface SpecificationVersionsProps {
  leadId: string
  currentSpec: string
  onSpecChange: (spec: string) => void
  onSpecBlur: () => void
}

export function SpecificationVersions({
  leadId,
  currentSpec,
  onSpecChange,
  onSpecBlur,
}: SpecificationVersionsProps) {
  const { data: versions, isLoading } = useSpecificationVersions(leadId)
  const createVersion = useCreateSpecificationVersion()
  const [showHistory, setShowHistory] = useState(false)
  const [viewingVersion, setViewingVersion] = useState<number | null>(null)

  const handleSaveVersion = () => {
    if (!currentSpec.trim()) return
    createVersion.mutate({
      leadId,
      content: currentSpec,
      changeSummary: 'Ручное обновление',
    })
  }

  const handleRevert = (content: string, version: number) => {
    onSpecChange(content)
    createVersion.mutate({
      leadId,
      content,
      changeSummary: `Откат к версии ${version}`,
    })
    setViewingVersion(null)
  }

  const viewedVersion = viewingVersion !== null
    ? versions?.find((v) => v.version === viewingVersion)
    : null

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider">
          Спецификация
        </label>
        <div className="flex items-center gap-2">
          {versions && versions.length > 0 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-1 text-xs text-text-secondary hover:text-white transition-colors"
            >
              <Clock className="w-3.5 h-3.5" />
              {versions.length} {versions.length === 1 ? 'версия' : versions.length < 5 ? 'версии' : 'версий'}
              {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
          <button
            onClick={handleSaveVersion}
            disabled={!currentSpec.trim() || createVersion.isPending}
            className="text-xs text-accent-lime hover:text-accent-lime/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {createVersion.isPending ? 'Сохранение...' : 'Сохранить версию'}
          </button>
        </div>
      </div>

      {/* Editor or Version Viewer */}
      {viewedVersion ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between px-3 py-2 bg-violet-500/10 border border-violet-500/20 rounded-t-lg">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-violet-400 font-medium">Версия {viewedVersion.version}</span>
              {viewedVersion.isAiGenerated && (
                <span className="flex items-center gap-1 text-violet-400">
                  <Sparkles className="w-3 h-3" /> AI
                </span>
              )}
              <span className="text-text-tertiary">
                {viewedVersion.creator?.username} · {formatDate(viewedVersion.createdAt)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleRevert(viewedVersion.content, viewedVersion.version)}
                className="flex items-center gap-1 text-xs text-accent-lime hover:text-accent-lime/80 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Откатить
              </button>
              <button
                onClick={() => setViewingVersion(null)}
                className="text-xs text-text-secondary hover:text-white transition-colors"
              >
                Закрыть
              </button>
            </div>
          </div>
          <div className="w-full h-32 px-4 py-3 bg-bg-tertiary border border-border-medium rounded-b-lg text-text-secondary text-sm whitespace-pre-wrap overflow-y-auto">
            {viewedVersion.content}
          </div>
        </div>
      ) : (
        <textarea
          value={currentSpec}
          onChange={(e) => onSpecChange(e.target.value)}
          onBlur={onSpecBlur}
          className="w-full h-32 px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20 resize-none"
          placeholder="Добавьте спецификацию или заметки..."
        />
      )}

      {/* Version History */}
      {showHistory && (
        <div className="mt-3 space-y-1.5">
          {isLoading ? (
            <div className="text-xs text-text-tertiary py-2">Загрузка версий...</div>
          ) : versions && versions.length > 0 ? (
            versions.map((v) => (
              <button
                key={v.id}
                onClick={() => setViewingVersion(v.version)}
                className={cn(
                  'w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors',
                  viewingVersion === v.version
                    ? 'bg-violet-500/10 border border-violet-500/20'
                    : 'bg-white/5 hover:bg-white/10 border border-transparent'
                )}
              >
                <span className="text-xs font-mono text-text-tertiary w-6 text-center">v{v.version}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    {v.isAiGenerated && <Sparkles className="w-3 h-3 text-violet-400 flex-shrink-0" />}
                    <span className="text-xs text-text-primary truncate">
                      {v.changeSummary || 'Без описания'}
                    </span>
                  </div>
                  <span className="text-[10px] text-text-tertiary">
                    {v.creator?.username || 'Система'} · {formatDate(v.createdAt)}
                  </span>
                </div>
              </button>
            ))
          ) : (
            <div className="text-xs text-text-tertiary py-2">Версий пока нет</div>
          )}
        </div>
      )}
    </div>
  )
}
